import React, { Fragment, useState, useEffect } from "react";
import {
    Box,
    Tabs,
    Tab,
    Grid,
    Card,
    CardMedia,
    CardContent,
    Typography,
    Select,
    MenuItem,
    TextField,
    IconButton,
    Breadcrumbs,
    useMediaQuery,
    CircularProgress,
    Backdrop,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Layout from "../components/layouts/Layout";
import * as api from "../utils/api";
import VendorFlatCard from "../components/products/VendorFlatCard";
import { useLocation } from "react-router-dom";  // Import useLocation to extract query params

const SearchPage = () => {
    const [tabValue, setTabValue] = useState(0);
    const [sortValue, setSortValue] = useState("desc");
    const [perPage, setPerPage] = useState(12);
    const [searchKeyword, setSearchKeyword] = useState("");  // Store the search keyword
    const [filteredData, setFilteredData] = useState({
        products: [],
        services: [],
        vendors: [],
    });
    const [isLoading, setIsLoading] = useState(false);

    const isMobile = useMediaQuery("(max-width:600px)");

    const location = useLocation();
    
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const queryKeyword = urlParams.get("keyword");
        if (queryKeyword) {
            setSearchKeyword(queryKeyword);  
        }
    }, [location]);

    // Function to fetch search results
    const fetchSearchResults = () => {
        setIsLoading(true);
        api
            .search({
                keyword: searchKeyword,
                sort: sortValue,
                type: "vendor",
            })
            .then((response) => {
                if (!response.error) {
                    const data = response;
                    setFilteredData({
                        products: data.products.data || [],
                        services: data.services.data || [],
                        vendors: data.vendors.data || [],
                    });
                } else {
                    setFilteredData({
                        products: [],
                        services: [],
                        vendors: [],
                    });
                }
                setIsLoading(false);
            })
            .catch(() => {
                setIsLoading(false);
                setFilteredData({
                    products: [],
                    services: [],
                    vendors: [],
                });
            });
    };

    // Debounce to fetch results after 500ms delay when searchKeyword changes
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchSearchResults();
        }, 500); // Debounce for 500ms

        return () => clearTimeout(timeoutId);
    }, [searchKeyword, sortValue, perPage]);

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // Determine which tab's data to display
    const tabData =
        tabValue === 0
            ? filteredData.products
            : tabValue === 1
            ? filteredData.services
            : filteredData.vendors;

    return (
        <Fragment>
            <Layout title={"Search"} logo={"/assets/images/logo.jpeg"}>
                <Breadcrumbs title={"Search"} crumb={"Accounts"} />
                <Box sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                    {/* Tabs */}
                    <Tabs value={tabValue} onChange={handleTabChange} centered>
                        <Tab label="Products" />
                        <Tab label="Services" />
                        <Tab label="Vendors" />
                    </Tabs>

                    {/* Search Field, Sorting, and Per Page Controls */}
                    <Box
                        sx={{
                            flexDirection: isMobile ? "column" : "row",
                            justifyContent: "space-between",
                            alignItems: isMobile ? "flex-start" : "center",
                            gap: 2,
                            mt: 3,
                        }}
                    >
                        <Typography>Search products, services, vendors</Typography>
                        <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                            <TextField
                                size="small"
                                variant="outlined"
                                placeholder="Search..."
                                value={searchKeyword}
                                onChange={(e) => setSearchKeyword(e.target.value)} // Update keyword on change
                                fullWidth
                            />
                            <IconButton>
                                <SearchIcon />
                            </IconButton>
                        </Box>

                        <Box sx={{ alignItems: "center", gap: 2, width: isMobile ? "100%" : "auto" }}>
                            <Typography>Sort</Typography>
                            <Select
                                size="small"
                                value={sortValue}
                                onChange={(e) => setSortValue(e.target.value)}
                                sx={{ width: "100px" }}
                            >
                                <MenuItem value="asc">ASC</MenuItem>
                                <MenuItem value="desc">DSC</MenuItem>
                            </Select>
                        </Box>

                        <Box sx={{ alignItems: "center", gap: 2, width: isMobile ? "100%" : "auto" }}>
                            <Typography>Per Page</Typography>
                            <Select
                                size="small"
                                value={perPage}
                                onChange={(e) => setPerPage(e.target.value)}
                                sx={{ width: "100px" }}
                            >
                                <MenuItem value={6}>6</MenuItem>
                                <MenuItem value={12}>12</MenuItem>
                                <MenuItem value={24}>24</MenuItem>
                            </Select>
                        </Box>
                    </Box>

                    {/* Loading Indicator */}
                    <Backdrop
                        open={isLoading}
                        sx={{
                            color: "#fff",
                            zIndex: (theme) => theme.zIndex.drawer + 1,
                        }}
                    >
                        <CircularProgress color="inherit" />
                    </Backdrop>

                    {/* Results Section */}
                    <Grid container spacing={3} sx={{ mt: 3 }}>
                        {tabData.length > 0 ? (
                            tabData.map((item, index) => (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                                    {tabValue === 2 ? ( // For Vendors tab, use VendorFlatCard
                                        <VendorFlatCard
                                            title={item.name}
                                            image={item.logo}
                                            des={item.description}
                                            addr={item.address}
                                            rating={item.rating}
                                            slug={item.slug}
                                            open={item.is_open}
                                        />
                                    ) : (
                                        // For Products and Services, use normal card
                                        <Card sx={{ boxShadow: 3 }}>
                                            <CardMedia
                                                component="img"
                                                height="140"
                                                image={item.photo}
                                                alt={item.name}
                                                sx={{ backgroundColor: "#eee" }}
                                            />
                                            <CardContent>
                                                <Typography variant="h6" fontWeight="bold">
                                                    {item.name}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    {item.vendor ? item.vendor.name : 'No vendor info'}
                                                </Typography>
                                                <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                                                    {item.price || item.discount_price}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    )}
                                </Grid>
                            ))
                        ) : (
                            <Typography variant="h6" color="textSecondary" sx={{ mt: 3, textAlign: "center", width: "100%" }}>
                                {isLoading ? "Searching..." : "No results found."}
                            </Typography>
                        )}
                    </Grid>
                </Box>
            </Layout>
        </Fragment>
    );
};

export default SearchPage;
